import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { useAuth } from "../../../contexts/auth";
import { baseAxios, Context } from "../../../utils/config";
import { colors } from "../../../theme/colors";
import {
  chartGrid,
  chartInk,
  chartLabel,
  useThemeMode,
} from "../../../contexts/theme-mode";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
  ChartDataLabels
);

const API = "gateway/oixti2bs73/";
const CONTROL_RADIUS = "6px";
const TOP_PRODUCTS = 12;

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(Math.round(n * 100) / 100).toFixed(2)} g`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid() || d.year() < 2000) return "—";
  return d.format("M/D/YY h:mm A");
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (field === "transaction_start") {
    return dayjs(va).valueOf() - dayjs(vb).valueOf();
  }
  if (
    typeof va === "number" ||
    typeof vb === "number" ||
    field === "transaction_entry_product_amount" ||
    field === "product_cost"
  ) {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Product Usage — recent product usage by salon / stylist / product
 */
export default function Reporting(props) {
  const { mode } = useThemeMode();
  const { location_name: filterLocationName } = props?.location?.state || {};
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // The last load failed; an empty table then means "unknown", not "no data".
  const [loadFailed, setLoadFailed] = useState(false);
  // Counts the loads. Typing a date starts one load per keystroke, and an
  // earlier, slower answer must not replace the newest one.
  const latestLoad = useRef(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("transaction_start");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    const thisLoad = ++latestLoad.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const payload = {
        Barcode: user.barcode,
        Role: user.userRoles || user.role || [],
        Organization: user.org,
        Location:
          globalLocation?.location_id && globalLocation.location_id !== -1
            ? globalLocation.location_id
            : user.location_id,
      };
      const result = await baseAxios.post(API, payload);
      if (thisLoad !== latestLoad.current) return;
      const data = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
          ? result.data.data
          : [];
      setRows(
        data.map((item, index) => ({
          ...item,
          rowKey: `${item.transaction_entry_id || item.transaction_id || "row"}-${index}`,
          transaction_entry_product_amount:
            Number(item.transaction_entry_product_amount) || 0,
          product_cost: Number(item.product_cost) || 0,
        }))
      );
    } catch (error) {
      if (thisLoad !== latestLoad.current) return;
      setRows([]);
      setLoadFailed(true);
      // baseAxios rejects with { status, data }, not with an axios error.
      if ((error?.status ?? error?.response?.status) === 403) {
        toast.error("Not allowed to see product usage data", { duration: 3000 });
      } else {
        toast.error("Error loading product usage data", { duration: 3000 });
      }
    } finally {
      if (thisLoad === latestLoad.current) setLoading(false);
    }
  }, [globalLocation?.location_id, user]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (filterLocationName) {
      list = list.filter(
        (row) =>
          String(row.location_name || "").toLowerCase() ===
          String(filterLocationName).toLowerCase()
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const haystack = [
        row.location_name,
        row.user_name,
        row.product_alias,
        row.brand_name,
        row.category_name,
        row.transaction_client_name,
        formatDate(row.transaction_start),
        formatQty(row.transaction_entry_product_amount),
        formatMoney(row.product_cost),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [filterLocationName, rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  const summary = useMemo(() => {
    const totalGrams = sortedRows.reduce(
      (sum, row) => sum + (Number(row.transaction_entry_product_amount) || 0),
      0
    );
    const totalCost = sortedRows.reduce(
      (sum, row) => sum + (Number(row.product_cost) || 0),
      0
    );
    const products = new Set(
      sortedRows.map((row) => row.product_alias).filter(Boolean)
    );
    return {
      totalGrams,
      totalCost,
      uniqueProducts: products.size,
      rows: sortedRows.length,
    };
  }, [sortedRows]);

  const chartData = useMemo(() => {
    const byProduct = new Map();
    sortedRows.forEach((row) => {
      const key = row.product_alias || "Unknown";
      const current = byProduct.get(key) || { amount: 0, cost: 0 };
      current.amount += Number(row.transaction_entry_product_amount) || 0;
      current.cost += Number(row.product_cost) || 0;
      byProduct.set(key, current);
    });
    const ranked = [...byProduct.entries()]
      .map(([label, values]) => ({ label, ...values }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, TOP_PRODUCTS);

    return {
      labels: ranked.map((item) => item.label),
      datasets: [
        {
          type: "bar",
          label: "Product used (g)",
          data: ranked.map((item) => Number(item.amount.toFixed(2))),
          backgroundColor: chartInk(mode),
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Cost",
          data: ranked.map((item) => Number(item.cost.toFixed(2))),
          backgroundColor: colors.gold,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };
  }, [sortedRows, mode]);

  const chartOptions = useMemo(
    () => {
      const labelColor = chartLabel(mode);
      return {
      responsive: true,
      maintainAspectRatio: false,
      color: labelColor,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 12, font: { weight: "600" }, color: labelColor },
        },
        title: {
          display: true,
          text: "Top products by usage",
          color: labelColor,
          font: { size: 14, weight: "600" },
          padding: { bottom: 12 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (ctx.dataset.label === "Cost") {
                return `${ctx.dataset.label}: ${formatMoney(v)}`;
              }
              return `${ctx.dataset.label}: ${v} g`;
            },
          },
        },
        datalabels: {
          display: (ctx) => (Number(ctx.dataset.data[ctx.dataIndex]) || 0) > 0,
          color: (ctx) =>
            ctx.dataset.backgroundColor === colors.gold
              ? mode === "dark"
                ? "#1a1a1a"
                : colors.black
              : "#ffffff",
          font: { weight: "600", size: 10 },
          anchor: "end",
          align: "start",
          formatter: (value, ctx) => {
            if (ctx.dataset.label === "Cost") return formatMoney(value);
            return `${value}`;
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: { size: 10 },
            color: labelColor,
            callback(value) {
              const label = this.getLabelForValue(value);
              return label.length > 18 ? `${label.slice(0, 16)}…` : label;
            },
          },
          grid: { display: false },
        },
        y: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Product used (g)", color: labelColor },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { color: chartGrid(mode) },
        },
        y1: {
          type: "linear",
          position: "right",
          title: { display: true, text: "Cost ($)", color: labelColor },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { drawOnChartArea: false },
        },
      },
    };
    },
    [mode]
  );

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Product Usage");
      worksheet.columns = [
        { header: "Salon", key: "location_name", width: 18 },
        { header: "Stylist", key: "user_name", width: 16 },
        { header: "Client", key: "transaction_client_name", width: 16 },
        { header: "Product", key: "product_alias", width: 36 },
        { header: "Date", key: "transaction_start", width: 18 },
        { header: "Amount (g)", key: "amount", width: 12 },
        { header: "Cost", key: "product_cost", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          location_name: row.location_name || "",
          user_name: row.user_name || "",
          transaction_client_name: row.transaction_client_name || "",
          product_alias: row.product_alias || "",
          transaction_start: formatDate(row.transaction_start),
          amount: Number(row.transaction_entry_product_amount) || 0,
          product_cost: Number(row.product_cost) || 0,
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ProductUsage_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "location_name",
        label: "Salon",
        field: "location_name",
        minWidth: 140,
        render: (v) => v || "—",
      },
      {
        id: "user_name",
        label: "Stylist",
        field: "user_name",
        minWidth: 130,
        render: (v) => v || "—",
      },
      {
        id: "product_alias",
        label: "Product",
        field: "product_alias",
        minWidth: 240,
        render: (v) => v || "—",
      },
      {
        id: "transaction_start",
        label: "Date",
        field: "transaction_start",
        minWidth: 140,
        render: (v) => formatDate(v),
      },
      {
        id: "transaction_entry_product_amount",
        label: "Amount Used",
        field: "transaction_entry_product_amount",
        minWidth: 120,
        render: (v) => formatQty(v),
      },
      {
        id: "product_cost",
        label: "Cost",
        field: "product_cost",
        minWidth: 100,
        render: (v) => (
          <Typography component="span" fontWeight={600} fontSize={14}>
            {formatMoney(v)}
          </Typography>
        ),
      },
    ],
    []
  );

  const locationLabel =
    filterLocationName ||
    (globalLocation?.location_id !== -1 ? globalLocation?.location_name : "") ||
    "";

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Product Usage
            </Typography>
            {locationLabel ? (
              <Chip
                size="small"
                label={locationLabel}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Review recent product usage by salon, stylist, and product (last ~32
            days).
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search salon, stylist, product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            aria-label="Refresh product usage"
            onClick={loadReport}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: CONTROL_RADIUS,
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !sortedRows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {[
          { label: "Total Used", value: formatQty(summary.totalGrams) },
          { label: "Total Cost", value: formatMoney(summary.totalCost) },
          { label: "Unique Products", value: String(summary.uniqueProducts) },
          { label: "Rows", value: String(summary.rows) },
        ].map((card) => (
          <Box
            key={card.label}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1.5,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {card.label}
            </Typography>
            <Typography fontWeight={700} sx={{ fontSize: "1.15rem", mt: 0.25 }}>
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          height: 320,
          mb: 2.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
          px: 2,
          py: 1.5,
        }}
      >
        {sortedRows.length ? (
          <Chart type="bar" data={chartData} options={chartOptions} />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              {loading
                ? "Loading chart…"
                : loadFailed
                ? "Could not load this report"
                : "No usage data to chart"}
            </Typography>
          </Box>
        )}
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="transaction_start"
        initialOrder="desc"
        searchQuery={`${search}|${filterLocationName || ""}|${globalLocation?.location_id ?? "all"}|${rows.length}`}
        clientSidePagination
        skeletonHeight="28px"
        getRowKey={(row) => row.rowKey}
        emptyMessage={
          loadFailed
            ? "Could not load this report. Try again."
            : "No product usage data available"
        }
      />
    </Box>
  );
}
